import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { SubjectService } from '../../../services';

@Component({
  selector: 'app-subjects',
  standalone: false,
  templateUrl: './subjects.html',
  styleUrl: './subjects.css',
})
export class Subjects implements OnInit {

  loading = false;
  errorMsg = '';
  subjects: any[] = [];
  filteredSubjects: any[] = [];
  pagedSubjects: any[] = [];

  allSubjects: any[] = [];

  searchQuery = '';
  page = 0;
  size = 10;

  showAddModal = false;
  showEditModal = false;
  showDeleteModal = false;

  selectedSubject: any = null;

  addForm = { name: '', code: '', icon: '', themeColor: '#000000' };
  editForm = { name: '', code: '', icon: '', themeColor: '#000000' };

  addLoading = false;
  editLoading = false;
  deleteLoading = false;
  statusLoadingId: number | null = null;

  addError = '';
  editError = '';
  deleteError = '';

  constructor(
    private subjectService: SubjectService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.fetchSubjects();
  }

  fetchSubjects() {
    this.loading = true;
    this.errorMsg = '';
    this.cdr.detectChanges();

    this.subjectService.getSubjects().subscribe({
      next: (res: any) => {
        this.allSubjects = res?.data ?? [];
        this.applyFilter();
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.errorMsg = err?.error?.message || 'Failed to load subjects';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  applyFilter() {
    const q = this.searchQuery.toLowerCase().trim();
    this.filteredSubjects = q
      ? this.allSubjects.filter(s =>
          s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q)
        )
      : [...this.allSubjects];
    this.page = 0;
    this.updatePage();
  }

  updatePage() {
    const start = this.page * this.size;
    this.pagedSubjects = this.filteredSubjects.slice(start, start + this.size);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredSubjects.length / this.size);
  }

  get hasNext(): boolean { return this.page < this.totalPages - 1; }
  get hasPrev(): boolean { return this.page > 0; }

  nextPage() {
    if (!this.hasNext) return;
    this.page++;
    this.updatePage();
  }

  prevPage() {
    if (!this.hasPrev) return;
    this.page--;
    this.updatePage();
  }

  onSearch() {
    this.applyFilter();
  }

  openAddModal() {
    this.addForm = { name: '', code: '', icon: '', themeColor: '#14B8A6' };
    this.addError = '';
    this.showAddModal = true;
  }

  closeAddModal() {
    if (this.addLoading) return;
    this.showAddModal = false;
    this.addError = '';
  }

  submitAdd() {
    if (!this.addForm.name.trim() || !this.addForm.code.trim()) {
      this.addError = 'Name and code are required.';
      return;
    }
    this.addLoading = true;
    this.addError = '';
    this.subjectService.addSubject(this.addForm).subscribe({
      next: () => {
        this.addLoading = false;
        this.showAddModal = false;
        this.fetchSubjects();
      },
      error: (err: any) => {
        this.addLoading = false;
        this.addError = err?.error?.message || 'Failed to add subject.';
        this.cdr.detectChanges();
      }
    });
  }

  openEditModal(subject: any) {
    this.selectedSubject = subject;
    this.editForm = {
      name: subject.name,
      code: subject.code,
      icon: subject.icon,
      themeColor: subject.themeColor
    };
    this.editError = '';
    this.showEditModal = true;
  }

  closeEditModal() {
    if (this.editLoading) return;
    this.showEditModal = false;
    this.editError = '';
    this.selectedSubject = null;
  }

  submitEdit() {
    if (!this.editForm.name.trim() || !this.editForm.code.trim()) {
      this.editError = 'Name and code are required.';
      return;
    }
    this.editLoading = true;
    this.editError = '';
    this.subjectService.updateSubject(this.selectedSubject.id, this.editForm).subscribe({
      next: () => {
        this.editLoading = false;
        this.showEditModal = false;
        this.fetchSubjects();
      },
      error: (err: any) => {
        this.editLoading = false;
        this.editError = err?.error?.message || 'Failed to update subject.';
        this.cdr.detectChanges();
      }
    });
  }

  openDeleteModal(subject: any) {
    this.selectedSubject = subject;
    this.deleteError = '';
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    if (this.deleteLoading) return;
    this.showDeleteModal = false;
    this.deleteError = '';
    this.selectedSubject = null;
  }

  confirmDelete() {
    if (!this.selectedSubject) return;
    this.deleteLoading = true;
    this.deleteError = '';
    this.subjectService.changeStatus(this.selectedSubject.id, 'INACTIVE').subscribe({
      next: () => {
        this.deleteLoading = false;
        this.showDeleteModal = false;
        this.fetchSubjects();
      },
      error: (err: any) => {
        this.deleteLoading = false;
        this.deleteError = err?.error?.message || 'Failed to delete subject.';
        this.cdr.detectChanges();
      }
    });
  }

  toggleStatus(subject: any) {
    if (this.statusLoadingId !== null) return;
    const newStatus = subject.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    this.statusLoadingId = subject.id;
    this.cdr.detectChanges();
    this.subjectService.changeStatus(subject.id, newStatus).subscribe({
      next: () => {
        this.statusLoadingId = null;
        this.fetchSubjects();
      },
      error: () => {
        this.statusLoadingId = null;
        this.cdr.detectChanges();
      }
    });
  }

  getStatusLabel(subject: any): string {
    return subject.status === 'ACTIVE' ? 'Active' : 'Inactive';
  }

  getMinValue(a: number, b: number) {
  return Math.min(a, b);
}
}